import React, { useState } from 'react';
import { 
  Code, 
  Cpu, 
  ShieldCheck, 
  Layers, 
  Settings, 
  Sparkles, 
  Zap, 
  TrendingUp, 
  ArrowRight,
  Database,
  Smartphone,
  Cloud,
  Globe,
  Terminal,
  MousePointer,
  Activity,
  CreditCard,
  PenTool,
  Search,
  CheckCircle2,
  Users
} from 'lucide-react';
import { COMPANY_SERVICES, ServiceItem } from './Services';

const HomeShowcase: React.FC = () => {
  // Service showcase state
  const [homeServiceFilter, setHomeServiceFilter] = useState<'all' | 'web-app' | 'enterprise' | 'cloud-api' | 'maintenance'>('all');
  const [activeServiceModal, setActiveServiceModal] = useState<ServiceItem | null>(null);

  // 1. Floating Elements Config
  const floatingAccents = [
    { icon: <Code className="h-6 w-6 text-brand-primary/10" />, className: "absolute top-10 left-10 animate-float" },
    { icon: <Database className="h-5 w-5 text-brand-accent/10" />, className: "absolute top-40 right-10 animate-float-delayed" },
    { icon: <Terminal className="h-6 w-6 text-brand-primary/10" />, className: "absolute bottom-10 left-12 animate-float" },
    { icon: <Cloud className="h-5 w-5 text-blue-500/10" />, className: "absolute bottom-40 right-20 animate-float-delayed" }
  ];

  // 2. Tab Selection for Calculator Section
  const [calcTab, setCalcTab] = useState<'estimator' | 'roi'>('estimator');

  // 3. Feature Showcase State
  const [activeTab, setActiveTab] = useState<'api' | 'ui' | 'cloud'>('api');

  // 4. Project Estimator State
  const [projectDuration, setProjectDuration] = useState<number>(3);
  const [teamSize, setTeamSize] = useState<number>(2);
  const [servicesSelected, setServicesSelected] = useState({
    web: true,
    mobile: false,
    backend: true,
    cloud: false
  });

  // 5. Outsourcing ROI Calculator State
  const [avgSalary, setAvgSalary] = useState<number>(90000);
  const [hiresCount, setHiresCount] = useState<number>(2);
  const [includeOverhead, setIncludeOverhead] = useState<boolean>(true);

  // 6. Interactive Architecture Mockup (Solution Builder) State
  const [archFeatures, setArchFeatures] = useState({
    chat: true,
    storage: false,
    payments: true,
    db: true
  });

  // 7. Tech Stack Hover State
  const [hoveredTech, setHoveredTech] = useState<string | null>(null);

  // 8. Roadmap Timeline State
  const [activeStep, setActiveStep] = useState<number>(0);

  // Calculations: Cost Estimator
  const calculateEstimate = () => {
    let baseCost = 4500; 
    let multiplier = 1.0;
    if (servicesSelected.web) multiplier += 0.2;
    if (servicesSelected.mobile) multiplier += 0.4;
    if (servicesSelected.backend) multiplier += 0.3;
    if (servicesSelected.cloud) multiplier += 0.3;

    const rawCost = projectDuration * teamSize * baseCost * multiplier;
    const minCost = Math.round(rawCost * 0.9);
    const maxCost = Math.round(rawCost * 1.1);

    let complexity = 'Low';
    if (teamSize >= 5 || projectDuration >= 6) complexity = 'High';
    else if (teamSize >= 3 || projectDuration >= 3) complexity = 'Medium';

    return {
      range: `$${minCost.toLocaleString()} - $${maxCost.toLocaleString()}`,
      complexity
    };
  };

  // Calculations: ROI Calculator
  const calculateROISavings = () => {
    const overheadMultiplier = includeOverhead ? 1.25 : 1.0;
    const inHouseCost = hiresCount * avgSalary * overheadMultiplier;
    
    const outsourcedRate = 45000; 
    const outsourcedCost = hiresCount * outsourcedRate;
    const annualSavings = inHouseCost - outsourcedCost;
    const percentage = Math.round((annualSavings / inHouseCost) * 100);

    return {
      inHouse: inHouseCost,
      outsourced: outsourcedCost,
      savings: annualSavings,
      percentage
    };
  };

  const estimate = calculateEstimate();
  const roi = calculateROISavings();

  const timelineSteps = [
    {
      title: '1. Discovery & Blueprint',
      desc: 'We conduct feasibility studies, map database relationship diagrams (ERDs), and lay down secure API mockups before code initialization.',
      detail: 'Deliverables: Architecture diagrams, UI wireframes, Swagger specifications.',
      icon: <Search className="h-5 w-5" />,
      status: 'Active Blueprint'
    },
    {
      title: '2. High-Fidelity UI/UX',
      desc: 'Our design sprints focus on producing sleek, responsive React layout prototypes to optimize conversion pathways and speed up frontend assembly.',
      detail: 'Deliverables: Figma interactive prototypes, brand guidelines.',
      icon: <PenTool className="h-5 w-5" />,
      status: 'Prototype Phase'
    },
    {
      title: '3. Agile Sprints',
      desc: 'Developers run bi-weekly code iterations directly tied to your JIRA dashboard. You check task logs, feature releases, and commits live.',
      detail: 'Deliverables: Sprint reviews, staging environment build deployments.',
      icon: <TrendingUp className="h-5 w-5" />,
      status: 'Sprint Execution'
    },
    {
      title: '4. Stress & Security Audits',
      desc: 'We run security vulnerability checks, sanitize MySQL database parameters against SQL Injection, and test loading performance to guarantee speed.',
      detail: 'Deliverables: Penetration logs, lighthouse speed audit reports.',
      icon: <ShieldCheck className="h-5 w-5" />,
      status: 'Vulnerability Audit'
    },
    {
      title: '5. Production Deployment',
      desc: 'The app is built, packaged into lightweight Docker containers, and hosted on secure AWS clusters under close uptime supervision.',
      detail: 'Deliverables: Docker files, AWS dashboard handover, SSL setups.',
      icon: <Cloud className="h-5 w-5" />,
      status: 'AWS Cloud Launch'
    }
  ];

  const techDescriptions: Record<string, string> = {
    '.NET (C#)': 'Enterprise-grade backend architectures, robust ERP systems, and secure RESTful services built on C# and ASP.NET Core.',
    'Java': 'Highly performant microservice engines, enterprise database routing pipelines, and secure cloud apps running on Spring Boot.',
    'AI & LLM Integration': 'Intelligent neural chat agents, predictive data classifiers, automated workflow models, and machine learning structures.',
    'Sitecore CMS': 'Enterprise content experience suite utilizing rich personalization modules and secure headless architecture setups.',
    'Umbraco CMS': 'Modern, highly flexible open-source CMS built on ASP.NET Core offering outstanding developer personalization controls.',
    'PHP (Budget-friendly)': 'Cost-effective, rapid MVC frameworks (Laravel/native) tailored for startups and businesses with limited scope parameters.',
    'React': 'High-performance SPA (Single Page Application) frontend layouts with smooth micro-interactions.',
    'TypeScript': 'Statical type safety ensuring zero runtime bugs and high codebase reliability.',
    'Node.js': 'Blazing fast scalable server architecture built for massive concurrent API traffic.',
    'Docker': 'Containerized packaging ensuring seamless alignment between staging and production.',
    'AWS Cloud': 'Secure scalable hosting setups with 99.9% uptime guarantees and robust encryption.'
  };

  return (
    <div className="relative overflow-hidden py-20 bg-white">
      {/* Background Accents */}
      {floatingAccents.map((item, index) => (
        <div key={index} className={item.className}>
          {item.icon}
        </div>
      ))}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-28 relative z-10">
        
        {/* ========================================================
            SECTION 1: CORE VALUE CARDS
           ======================================================== */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-secondary">
            Engineering Solutions for <span className="text-brand-primary">Unmatched Results</span>
          </h2>
          <p className="text-gray-600 text-lg">
            We merge clean code with architectural excellence to design robust custom software platforms.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: <Sparkles className="h-8 w-8 text-brand-primary" />,
              title: 'Tailored Customer UX',
              desc: 'Every layout is designed from scratch around your brand identity. Zero generic templates, maximum user retention.'
            },
            {
              icon: <Zap className="h-8 w-8 text-brand-primary" />,
              title: 'Production-Grade Cloud',
              desc: 'Containerized deployment via Docker and AWS ensuring your APIs scale cleanly with high traffic spikes.'
            },
            {
              icon: <TrendingUp className="h-8 w-8 text-brand-primary" />,
              title: 'Agile Transparency',
              desc: 'Daily task updates integrated directly with our Jira tracking systems. Complete vision alignment at every step.'
            }
          ].map((item, idx) => (
            <div 
              key={idx}
              className="p-8 bg-brand-light/35 rounded-2xl border border-green-100 hover:border-brand-primary transition-all duration-300 transform hover:scale-[1.03] hover:shadow-xl space-y-4 cursor-pointer"
            >
              <div className="p-3 bg-white w-fit rounded-xl shadow-md">{item.icon}</div>
              <h3 className="text-xl font-bold text-brand-secondary">{item.title}</h3>
              <p className="text-gray-600 leading-relaxed text-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* ========================================================
            SERVICES SHOWCASE: 8 COMPANY SERVICE PILLARS
           ======================================================== */}
        <section id="home-services" className="space-y-10 scroll-mt-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-gray-100 pb-6">
            <div className="space-y-3 max-w-2xl">
              <span className="bg-brand-primary/10 text-brand-dark px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-brand-primary" />
                Company Capabilities
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-brand-secondary tracking-tight">
                Our Core <span className="text-brand-primary">Services & Solutions</span>
              </h2>
              <p className="text-gray-600 text-sm md:text-base">
                Explore the 8 comprehensive technology pillars we provide to startups, enterprises, and institutions.
              </p>
            </div>

            {/* Category Filter Pills */}
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'all', label: 'All Services (8)' },
                { id: 'web-app', label: 'Web & Mobile' },
                { id: 'enterprise', label: 'CRM / ERP & SaaS' },
                { id: 'cloud-api', label: 'Cloud & APIs' },
                { id: 'maintenance', label: 'AMC & Support' }
              ].map((pill) => (
                <button
                  key={pill.id}
                  onClick={() => setHomeServiceFilter(pill.id as any)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 ${
                    homeServiceFilter === pill.id
                      ? 'bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] text-white shadow-md shadow-[#14c9cb]/25 scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-teal-50 hover:text-[#0d9488] border border-transparent'
                  }`}
                >
                  {pill.label}
                </button>
              ))}
            </div>
          </div>

          {/* Grid of 8 Services */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {COMPANY_SERVICES
              .filter(s => homeServiceFilter === 'all' || s.category === homeServiceFilter)
              .map((service) => (
                <div
                  key={service.id}
                  className="bg-white rounded-2xl border border-gray-200/80 p-6 hover:shadow-xl hover:border-[#14c9cb] transition-all duration-300 flex flex-col justify-between group hover:-translate-y-1 relative"
                >
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="p-3 bg-[#f0fdf9] text-[#0d9488] rounded-xl group-hover:bg-gradient-to-r group-hover:from-[#10b981] group-hover:to-[#14c9cb] group-hover:text-white transition-all shadow-sm">
                        {service.icon}
                      </div>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#f0fdf9] text-[#0d9488] border border-[#14c9cb]/30 uppercase tracking-wide">
                        {service.badge}
                      </span>
                    </div>

                    {/* Title */}
                    <div>
                      <h3 className="text-base font-bold text-brand-secondary group-hover:text-brand-primary transition-colors line-clamp-1">
                        {service.title}
                      </h3>
                      <p className="text-[11px] font-medium text-brand-accent mt-0.5 line-clamp-1">
                        {service.subtitle}
                      </p>
                    </div>

                    <p className="text-gray-500 text-xs leading-relaxed line-clamp-3">
                      {service.description}
                    </p>

                    {/* Key Offerings Preview */}
                    <div className="space-y-1.5 pt-2 border-t border-gray-100">
                      <div className="text-[10px] font-bold uppercase text-gray-400 tracking-wider">
                        Key Offerings:
                      </div>
                      {service.offerings.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex items-start gap-1.5 text-xs text-gray-700">
                          <CheckCircle2 className="h-3.5 w-3.5 text-brand-primary flex-shrink-0 mt-0.5" />
                          <span className="line-clamp-1 text-[11px]">{item}</span>
                        </div>
                      ))}
                      {service.offerings.length > 3 && (
                        <p className="text-[10px] font-semibold text-brand-primary pl-5">
                          +{service.offerings.length - 3} more offerings
                        </p>
                      )}
                    </div>

                    {/* Tech Stacks Tags */}
                    {service.technologies && (
                      <div className="flex flex-wrap gap-1 pt-1">
                        {service.technologies.slice(0, 4).map((tech, idx) => (
                          <span
                            key={idx}
                            className="px-1.5 py-0.5 rounded bg-gray-50 text-gray-600 text-[10px] font-medium border border-gray-200"
                          >
                            {tech}
                          </span>
                        ))}
                        {service.technologies.length > 4 && (
                          <span className="px-1.5 py-0.5 rounded bg-gray-50 text-gray-400 text-[10px]">
                            +{service.technologies.length - 4}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-5 pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                    <button
                      onClick={() => setActiveServiceModal(service)}
                      className="text-xs font-semibold text-gray-600 hover:text-brand-primary transition-colors underline-offset-2 hover:underline"
                    >
                      Quick View
                    </button>
                    <button
                      onClick={() => { window.location.hash = '#contact'; }}
                      className="flex items-center text-xs font-bold text-brand-primary hover:text-brand-dark group-hover:translate-x-0.5 transition-all"
                    >
                      Get Quote
                      <ArrowRight className="ml-1 h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))}
          </div>

          {/* Quick Nav Footer to Full Services Page */}
          <div className="flex flex-col sm:flex-row items-center justify-between p-6 bg-gradient-to-r from-teal-50/70 via-[#f0fdf9] to-cyan-50/70 rounded-2xl border border-[#14c9cb]/30 gap-4 shadow-sm">
            <div className="flex items-center gap-3 text-center sm:text-left">
              <div className="p-2.5 bg-gradient-to-r from-[#10b981] to-[#14c9cb] text-white rounded-xl shadow-sm hidden sm:block">
                <Globe className="h-5 w-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-800">
                  Want to explore detailed scopes, tech stacks & architecture blueprints?
                </h4>
                <p className="text-xs text-slate-600">
                  Browse our full interactive services catalogue with in-depth breakdowns.
                </p>
              </div>
            </div>
            <button
              onClick={() => { window.location.hash = '#services'; }}
              className="px-5 py-2.5 bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] hover:shadow-[0_6px_20px_rgba(20,201,203,0.3)] text-white rounded-xl text-xs font-bold transition-all shadow-md flex items-center gap-2 whitespace-nowrap transform hover:scale-[1.02]"
            >
              Explore All 8 Services
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>

        {/* ========================================================
            SECTION 2: INTERACTIVE FEATURE MOCKUP
           ======================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h3 className="text-2xl md:text-3xl font-bold text-brand-secondary leading-tight">
              See Our Engineering <br />
              <span className="text-brand-primary">In Real Time</span>
            </h3>
            <p className="text-gray-600">
              Select an architectural focus below to see how our engineering stack manages data integrity, page speeds, and security.
            </p>

            <div className="flex flex-col space-y-3">
              {[
                { id: 'api', title: 'Custom RESTful APIs', desc: 'Secure database endpoints built for high throughput.' },
                { id: 'ui', title: 'Pixel-Perfect Responsive UI', desc: 'Seamless CSS transitions across tablets and mobiles.' },
                { id: 'cloud', title: 'Scalable Containerization', desc: 'Docker architectures hosting microservices cleanly.' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`p-4 text-left rounded-xl border transition-all duration-200 ${
                    activeTab === tab.id 
                      ? 'bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] text-white border-transparent shadow-lg shadow-[#14c9cb]/25 scale-[1.01]' 
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <p className="font-semibold">{tab.title}</p>
                  <p className={`text-xs mt-1 ${activeTab === tab.id ? 'text-teal-50' : 'text-gray-500'}`}>{tab.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Interactive Code / Architecture Console Window */}
          <div className="bg-white/95 backdrop-blur-md rounded-2xl shadow-xl border-2 border-[#14c9cb]/50 overflow-hidden flex flex-col h-[340px] relative transition-all duration-300 hover:shadow-2xl hover:border-[#14c9cb]">
            {/* Ambient subtle glow */}
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-[#14c9cb]/15 rounded-full blur-2xl pointer-events-none"></div>

            {/* Terminal Header */}
            <div className="bg-gradient-to-r from-teal-50 via-emerald-50 to-cyan-50 px-4 py-3 flex items-center justify-between border-b border-[#14c9cb]/30 z-10">
              <div className="flex space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-400 shadow-sm"></div>
                <div className="w-3 h-3 rounded-full bg-amber-400 shadow-sm"></div>
                <div className="w-3 h-3 rounded-full bg-[#10b981] shadow-sm"></div>
              </div>
              <div className="text-xs text-[#0f766e] font-mono font-bold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-[#14c9cb] animate-ping"></span>
                <span>x2infotech-architecture-panel</span>
              </div>
              <span className="text-[10px] font-mono bg-[#14c9cb]/20 text-[#0d9488] px-2 py-0.5 rounded-full font-bold">
                ACTIVE
              </span>
            </div>

            {/* Display Body */}
            <div className="p-6 font-mono text-sm flex-grow bg-gradient-to-br from-white via-emerald-50/30 to-green-50/40 tech-grid-bg overflow-y-auto space-y-4 z-10">
              {activeTab === 'api' && (
                <div className="space-y-2 animate-fade-in">
                  <p className="text-emerald-700 font-semibold">// API Controller initializing...</p>
                  <p className="text-blue-700 font-bold">GET /api/auth_login.php HTTP/1.1</p>
                  <div className="inline-flex items-center gap-2 px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold border border-emerald-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                    Response Status: 200 OK
                  </div>
                  <pre className="text-slate-800 text-xs bg-white/95 p-3.5 rounded-xl border border-emerald-200 shadow-sm">
{`{
  "status": true,
  "message": "Login successful",
  "token": "4a9f8f2b...",
  "user": { "role": "admin" }
}`}
                  </pre>
                </div>
              )}
              {activeTab === 'ui' && (
                <div className="space-y-2 animate-fade-in">
                  <p className="text-emerald-700 font-semibold">/* Tailwind CSS rendering viewport */</p>
                  <p className="text-purple-700 font-bold">@media (max-width: 768px) {'{'}</p>
                  <p className="text-slate-700 pl-4 font-medium">.nav-menu {'{'} display: none; {'}'}</p>
                  <p className="text-slate-700 pl-4 font-medium">.mobile-widget {'{'} transform: scale(1.05); {'}'}</p>
                  <p className="text-purple-700 font-bold">{'}'}</p>
                  <div className="p-3 bg-emerald-100/90 rounded-xl border border-emerald-300 text-center flex items-center justify-center space-x-2 text-xs text-emerald-900 font-semibold shadow-sm">
                    <span className="animate-bounce">📱</span>
                    <span>Responsive alignment compiled dynamically!</span>
                  </div>
                </div>
              )}
              {activeTab === 'cloud' && (
                <div className="space-y-2 animate-fade-in bg-white/95 p-4 rounded-xl border border-emerald-200 shadow-sm">
                  <p className="text-emerald-700 font-bold">$ docker-compose up -d --build</p>
                  <p className="text-slate-600">Building api-server...</p>
                  <p className="text-blue-700 font-medium">Step 4/6 : COPY ./api /var/www/html/api</p>
                  <p className="text-emerald-600 font-medium flex items-center gap-1.5">
                    <span className="text-emerald-500">✔</span> Creating x2_mysql_db ... done
                  </p>
                  <p className="text-emerald-800 font-bold flex items-center gap-2 bg-emerald-50 p-2 rounded-lg border border-emerald-200">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    Creating x2_web_app ... running on port 8080
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ========================================================
            SECTION 3: REFINED COLLABORATIVE ROADMAP WITH SVG CONNECTORS
           ======================================================== */}
        <div className="bg-brand-light/20 border border-green-150 p-8 md:p-12 rounded-3xl space-y-10 relative overflow-hidden">
          {/* Ambient Glow behind roadmap */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-brand-primary/5 rounded-full filter blur-3xl pointer-events-none"></div>

          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="bg-brand-primary/10 text-brand-dark px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Our Process Flow</span>
            <h3 className="text-2xl md:text-3xl font-bold text-brand-secondary">
              Collaborative Journey <span className="text-brand-primary">Roadmap</span>
            </h3>
            <p className="text-gray-600 text-sm">
              An animated walk-through of how we transition ideas into robust enterprise deployments.
            </p>
          </div>

          {/* Timeline Pipeline */}
          <div className="relative flex flex-col md:flex-row justify-between items-center max-w-5xl mx-auto py-8">
            {/* SVG Connecting pipeline path */}
            <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-200 -translate-y-1/2 hidden md:block z-0">
              <div 
                className="bg-brand-primary h-full transition-all duration-700 ease-out" 
                style={{ width: `${(activeStep / 4) * 100}%` }}
              ></div>
            </div>

            {timelineSteps.map((step, idx) => (
              <button
                key={idx}
                onClick={() => setActiveStep(idx)}
                className="relative z-10 flex flex-col items-center group cursor-pointer focus:outline-none py-4 md:py-0"
              >
                {/* Milestone Node */}
                <div className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transform transition-all duration-350 border-4 ${
                  activeStep === idx 
                    ? 'bg-brand-primary border-brand-light text-white scale-110 shadow-brand-primary/30' 
                    : 'bg-white border-gray-150 text-gray-500 hover:border-brand-primary hover:text-brand-primary group-hover:scale-105'
                }`}>
                  {step.icon}

                  {/* Pulsing Active Radar Indicator */}
                  {activeStep === idx && (
                    <span className="absolute -inset-1 rounded-full bg-brand-accent/20 animate-ping -z-10"></span>
                  )}
                </div>

                <div className="mt-3 text-center md:absolute md:top-16 md:w-32">
                  <p className={`text-xs font-bold tracking-tight transition-colors duration-200 ${
                    activeStep === idx ? 'text-brand-primary font-extrabold' : 'text-gray-600'
                  }`}>
                    {step.title.split('. ')[1]}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {/* Stepper Content Glass Box */}
          <div className="p-6 md:p-10 bg-white/70 backdrop-blur-md rounded-2xl border border-green-100 shadow-2xl grid grid-cols-1 md:grid-cols-3 gap-6 items-center min-h-[190px] relative z-10 transition-all duration-300 hover:shadow-brand-primary/5">
            <div className="md:col-span-2 space-y-4 animate-fade-in">
              <div className="flex items-center space-x-3">
                <span className="bg-brand-primary text-white text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                  Phase {activeStep + 1}
                </span>
                <span className="text-gray-400 text-xs">|</span>
                <span className="text-brand-dark text-xs font-extrabold tracking-wider uppercase">
                  {timelineSteps[activeStep].status}
                </span>
              </div>
              <h4 className="text-2xl font-bold text-brand-secondary">{timelineSteps[activeStep].title}</h4>
              <p className="text-gray-600 leading-relaxed text-sm">
                {timelineSteps[activeStep].desc}
              </p>
            </div>
            
            <div className="bg-brand-light/80 p-5 rounded-2xl border border-green-200/60 flex flex-col justify-center space-y-2">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="h-4 w-4 text-brand-dark" />
                <span className="text-xs font-bold uppercase tracking-wider text-brand-dark">Milestone Output</span>
              </div>
              <span className="text-gray-700 text-xs italic leading-relaxed">
                {timelineSteps[activeStep].detail}
              </span>
            </div>
          </div>
        </div>

        {/* ========================================================
            SECTION 4: ESTIMATOR & ROI SAVINGS DUAL CALCULATOR
            Refactored to Emerald & White Theme with Video-like Shimmer
           ======================================================== */}
        <div className="relative p-8 md:p-12 bg-gradient-to-br from-white via-emerald-50/40 to-green-50/60 rounded-3xl shadow-2xl shadow-emerald-700/5 border border-emerald-200/80 space-y-8 overflow-hidden">
          {/* Ambient Video-style Glow Aura */}
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-emerald-300/20 rounded-full blur-3xl pointer-events-none animate-aura-pulse"></div>
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-green-200/30 rounded-full blur-3xl pointer-events-none animate-aura-pulse" style={{ animationDelay: '3s' }}></div>

          {/* Shimmer Light-Sweep Effect */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="w-1/2 h-full bg-gradient-to-r from-transparent via-white/50 to-transparent animate-shimmer-sweep"></div>
          </div>

          <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6 border-b border-emerald-100 pb-6">
            <div className="space-y-1 text-center md:text-left">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/70 border border-emerald-200 text-emerald-800 text-xs font-bold uppercase tracking-wider mb-1">
                <Sparkles className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
                Live Budget & Value Modeler
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900">
                Project Scope & <span className="text-emerald-600">Business Analytics</span>
              </h3>
              <p className="text-slate-600 text-xs md:text-sm">
                Analyze estimated budgets or calculate dynamic savings compared to in-house local hiring.
              </p>
            </div>
            {/* Toggles */}
            <div className="flex bg-[#f0fdf9] p-1.5 rounded-xl border border-[#14c9cb]/30 shadow-inner">
              <button
                onClick={() => setCalcTab('estimator')}
                className={`px-4 py-2 rounded-lg font-bold text-xs transition-all duration-200 ${
                  calcTab === 'estimator' 
                    ? 'bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] text-white shadow-md shadow-[#14c9cb]/30' 
                    : 'text-slate-600 hover:text-[#0d9488]'
                }`}
              >
                Cost Estimator
              </button>
              <button
                onClick={() => setCalcTab('roi')}
                className={`px-4 py-2 rounded-lg font-bold text-xs transition-all duration-200 ${
                  calcTab === 'roi' 
                    ? 'bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] text-white shadow-md shadow-[#14c9cb]/30' 
                    : 'text-slate-600 hover:text-[#0d9488]'
                }`}
              >
                Outsourcing ROI
              </button>
            </div>
          </div>

          {calcTab === 'estimator' ? (
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 animate-fade-in">
              {/* Controls */}
              <div className="space-y-6">
                <div className="space-y-3">
                  <p className="font-semibold text-sm text-slate-700">Select Services Required</p>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { key: 'web', label: 'Web Application', icon: <Globe className="h-4 w-4" /> },
                      { key: 'mobile', label: 'Mobile App (iOS/Android)', icon: <Smartphone className="h-4 w-4" /> },
                      { key: 'backend', label: 'REST API & Database', icon: <Database className="h-4 w-4" /> },
                      { key: 'cloud', label: 'AWS Hosting Setup', icon: <Cloud className="h-4 w-4" /> }
                    ].map((srv) => {
                      const isChecked = servicesSelected[srv.key as keyof typeof servicesSelected];
                      return (
                        <label 
                          key={srv.key}
                          className={`flex items-center p-3.5 rounded-xl border-2 cursor-pointer transition-all duration-200 transform hover:-translate-y-0.5 ${
                            isChecked
                              ? 'bg-white border-emerald-500 text-slate-900 shadow-md shadow-emerald-500/10'
                              : 'bg-white/70 border-slate-200 text-slate-600 hover:border-emerald-300 hover:bg-emerald-50/30'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => setServicesSelected(prev => ({ ...prev, [srv.key]: e.target.checked }))}
                            className="hidden"
                          />
                          <div className={`p-1.5 rounded-lg mr-2.5 transition-colors ${
                            isChecked ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {srv.icon}
                          </div>
                          <span className="text-xs font-semibold flex-1">{srv.label}</span>
                          {isChecked && (
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 ml-1" />
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700 font-semibold">Project Duration</span>
                      <span className="bg-emerald-100 text-emerald-800 font-bold px-2.5 py-0.5 rounded-full text-xs">
                        {projectDuration} Month(s)
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="12"
                      value={projectDuration}
                      onChange={(e) => setProjectDuration(Number(e.target.value))}
                      className="w-full h-2.5 bg-emerald-100 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700 font-semibold">Development Team Size</span>
                      <span className="bg-emerald-100 text-emerald-800 font-bold px-2.5 py-0.5 rounded-full text-xs">
                        {teamSize} Developer(s)
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={teamSize}
                      onChange={(e) => setTeamSize(Number(e.target.value))}
                      className="w-full h-2.5 bg-emerald-100 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                    />
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="relative bg-white/95 backdrop-blur-md p-6 rounded-2xl border-2 border-emerald-400/40 shadow-xl shadow-emerald-600/10 flex flex-col justify-between overflow-hidden">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase tracking-wider text-emerald-800 font-extrabold flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-600"></span>
                      </span>
                      Estimated Cost & Scale
                    </p>
                    <span className="text-[10px] bg-emerald-50 text-emerald-700 font-bold px-2 py-0.5 rounded-md border border-emerald-200">
                      Live Compute
                    </span>
                  </div>

                  <div className="text-3xl md:text-4xl font-black text-emerald-700 tracking-tight">
                    {estimate.range}
                  </div>

                  <div className="flex justify-between border-t border-emerald-100 pt-3 text-sm">
                    <span className="text-slate-500">Technical Complexity:</span>
                    <span className={`font-bold uppercase tracking-wider px-2 py-0.5 rounded-md text-xs ${
                      estimate.complexity === 'High' 
                        ? 'bg-red-50 text-red-600 border border-red-200' 
                        : estimate.complexity === 'Medium' 
                        ? 'bg-amber-50 text-amber-600 border border-amber-200' 
                        : 'bg-emerald-50 text-emerald-600 border border-emerald-200'
                    }`}>
                      {estimate.complexity}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Suggested Architecture:</span>
                    <span className="text-slate-800 font-semibold">
                      {teamSize >= 4 ? 'Microservices (Docker)' : 'Modular Monolith (MVC)'}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => { window.location.hash = '#contact'; }}
                  className="mt-6 w-full bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] hover:shadow-[0_8px_25px_rgba(20,201,203,0.35)] text-white py-3.5 rounded-xl font-bold shadow-lg transition-all duration-200 flex items-center justify-center group transform hover:scale-[1.01]"
                >
                  Discuss Scope & Get Proposal
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1.5 transition-transform duration-200" />
                </button>
              </div>
            </div>
          ) : (
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 animate-fade-in">
              {/* Controls */}
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700 font-semibold">In-House Developer Salary</span>
                      <span className="bg-emerald-100 text-emerald-800 font-bold px-2.5 py-0.5 rounded-full text-xs">
                        ${avgSalary.toLocaleString()}/yr
                      </span>
                    </div>
                    <input
                      type="range"
                      min="50000"
                      max="150000"
                      step="5000"
                      value={avgSalary}
                      onChange={(e) => setAvgSalary(Number(e.target.value))}
                      className="w-full h-2.5 bg-emerald-100 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-700 font-semibold">Developers Needed</span>
                      <span className="bg-emerald-100 text-emerald-800 font-bold px-2.5 py-0.5 rounded-full text-xs">
                        {hiresCount} Hires
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="5"
                      value={hiresCount}
                      onChange={(e) => setHiresCount(Number(e.target.value))}
                      className="w-full h-2.5 bg-emerald-100 rounded-lg appearance-none cursor-pointer accent-emerald-600"
                    />
                  </div>
                </div>

                <label className="flex items-center space-x-3 cursor-pointer p-3 rounded-xl bg-white border border-emerald-100 shadow-sm hover:border-emerald-300 transition-colors">
                  <input
                    type="checkbox"
                    checked={includeOverhead}
                    onChange={(e) => setIncludeOverhead(e.target.checked)}
                    className="h-4 w-4 rounded text-emerald-600 focus:ring-0 accent-emerald-600"
                  />
                  <span className="text-xs text-slate-700 font-medium">
                    Include 25% overhead (taxes, equipment, desk space, employee benefits)
                  </span>
                </label>
              </div>

              {/* Savings Results */}
              <div className="relative bg-white/95 backdrop-blur-md p-6 rounded-2xl border-2 border-emerald-400/40 shadow-xl shadow-emerald-600/10 flex flex-col justify-between overflow-hidden">
                <div className="space-y-4">
                  <p className="text-xs uppercase tracking-wider text-emerald-800 font-extrabold flex items-center gap-1.5">
                    <TrendingUp className="w-4 h-4 text-emerald-600" />
                    Annual Savings Analysis
                  </p>
                  
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-slate-600">
                        <span>In-House Cost:</span>
                        <span className="font-bold text-slate-900">${roi.inHouse.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden border border-slate-200">
                        <div className="bg-red-400 h-full rounded-full transition-all duration-300" style={{ width: '100%' }}></div>
                      </div>
                    </div>

                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-slate-600">
                        <span>Outsource to X2InfoTech:</span>
                        <span className="font-bold text-emerald-700">${roi.outsourced.toLocaleString()}</span>
                      </div>
                      <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden border border-slate-200">
                        <div className="bg-emerald-500 h-full rounded-full transition-all duration-300" style={{ width: `${(roi.outsourced / roi.inHouse) * 100}%` }}></div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-emerald-100 pt-4 flex items-center justify-between">
                    <span className="text-slate-600 text-sm font-medium">Total Saved Annually:</span>
                    <span className="text-xl md:text-2xl font-black text-emerald-700">
                      ${roi.savings.toLocaleString()} ({roi.percentage}% Saved)
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => { window.location.hash = '#contact'; }}
                  className="mt-6 w-full bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] hover:shadow-[0_8px_25px_rgba(20,201,203,0.35)] text-white py-3.5 rounded-xl font-bold shadow-lg transition-all duration-200 flex items-center justify-center group transform hover:scale-[1.01]"
                >
                  Acquire Managed Team Now
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1.5 transition-transform duration-200" />
                </button>
              </div>
            </div>
          )}
          <div className="relative z-10 text-center pt-4 border-t border-emerald-100 w-full">
            <p className="text-[10px] text-slate-500 italic">
              Note: This is a standard calculator provided for preliminary estimation purposes only. Final budgets, complexity ratings, and project scopes will be officially determined after comprehensive analysis and discovery of your system requirements.
            </p>
          </div>
        </div>

        {/* ========================================================
            SECTION 5: INTERACTIVE SOLUTION BUILDER (SYSTEM ARCHITECTURE MOCKUP)
            Refactored to Emerald/White Theme with Awwwards-style SVG Laser Streams
           ======================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <span className="bg-emerald-100 text-emerald-800 border border-emerald-200 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 shadow-xs">
              <Sparkles className="w-3.5 h-3.5 text-emerald-600 animate-pulse" />
              Solution Blueprint Creator
            </span>
            <h3 className="text-2xl md:text-3xl font-bold text-slate-900 leading-tight">
              Interactive System <br />
              <span className="text-emerald-600">Architecture Mockup</span>
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              Toggle specific features below to see how our engineering engine routes real-time messaging, distributed cloud storage, payment pipelines, and secure databases in live video-rendered motion.
            </p>

            {/* Feature Toggles */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { key: 'chat', label: 'Real-time Messaging (WebSockets)', icon: <Activity className="h-4 w-4" /> },
                { key: 'storage', label: 'Scalable Cloud Storage (AWS S3)', icon: <Cloud className="h-4 w-4" /> },
                { key: 'payments', label: 'Payments Processing (Stripe)', icon: <CreditCard className="h-4 w-4" /> },
                { key: 'db', label: 'Secure MySQL DB Cluster', icon: <Database className="h-4 w-4" /> }
              ].map((feature) => {
                const isSelected = archFeatures[feature.key as keyof typeof archFeatures];
                return (
                  <button
                    key={feature.key}
                    onClick={() => setArchFeatures(prev => ({ ...prev, [feature.key]: !prev[feature.key as keyof typeof archFeatures] }))}
                    className={`p-4 text-left rounded-xl border-2 transition-all duration-300 flex flex-col justify-between h-24 transform hover:-translate-y-0.5 ${
                      isSelected
                        ? 'bg-emerald-50/90 text-slate-900 border-emerald-500 shadow-md shadow-emerald-500/15 scale-[1.02]'
                        : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/20'
                    }`}
                  >
                    <div className="flex justify-between items-center w-full">
                      <div className={`p-1.5 rounded-lg transition-colors ${
                        isSelected ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
                      }`}>
                        {feature.icon}
                      </div>
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                        isSelected ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-transparent border-slate-300'
                      }`}>
                        {isSelected && <div className="w-1.5 h-1.5 bg-white rounded-full"></div>}
                      </div>
                    </div>
                    <span className="text-[11px] font-bold leading-tight">{feature.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Dynamic Architecture Visualizer Canvas (Awwwards Video Style) */}
          <div className="relative bg-gradient-to-br from-white via-emerald-50/50 to-green-50/70 border-2 border-emerald-300/80 p-4 rounded-3xl h-[370px] flex flex-col justify-between shadow-2xl shadow-emerald-700/10 overflow-hidden tech-grid-bg">
            {/* Ambient Shifting Glow Auras */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-emerald-400/15 rounded-full blur-3xl pointer-events-none animate-aura-pulse"></div>
            <div className="absolute -top-10 -right-10 w-44 h-44 bg-blue-300/15 rounded-full blur-2xl pointer-events-none"></div>
            <div className="absolute -bottom-10 -left-10 w-44 h-44 bg-green-300/20 rounded-full blur-2xl pointer-events-none"></div>

            {/* Canvas Header Telemetry Bar */}
            <div className="relative z-10 flex items-center justify-between px-2 pt-1 border-b border-emerald-200/60 pb-2">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-600"></span>
                </span>
                <span className="text-[10px] font-bold text-emerald-900 uppercase tracking-wider font-mono">
                  Live System Blueprint
                </span>
              </div>
              <div className="flex items-center gap-2 text-[10px] font-mono text-emerald-800 font-semibold">
                <span className="bg-white/90 px-2 py-0.5 rounded-full border border-emerald-200 shadow-xs">
                  60 FPS STREAM
                </span>
              </div>
            </div>

            {/* SVG Connector paths with Animated Laser Beams and Traveling Light Packets */}
            <svg className="w-full h-full z-0" viewBox="0 0 500 300" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <filter id="laser-glow-green" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="2.5" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <filter id="laser-glow-blue" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="2.5" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
                <filter id="laser-glow-purple" x="-20%" y="-20%" width="140%" height="140%">
                  <feGaussianBlur stdDeviation="2.5" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Inactive Base Tracks (Subtle dotted line) */}
              <path d="M 250,150 L 85,45" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="3 3" fill="none" opacity="0.6" />
              <path d="M 250,150 L 415,45" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="3 3" fill="none" opacity="0.6" />
              <path d="M 250,150 L 85,255" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="3 3" fill="none" opacity="0.6" />
              <path d="M 250,150 L 415,255" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="3 3" fill="none" opacity="0.6" />

              {/* Chat Node Laser Stream */}
              {archFeatures.chat && (
                <>
                  <path d="M 250,150 L 85,45" stroke="#10b981" strokeWidth="3" fill="none" strokeDasharray="10 6" className="animate-laser-flow" filter="url(#laser-glow-green)" />
                  <circle r="4" fill="#059669">
                    <animateMotion path="M 250,150 L 85,45" dur="1.3s" repeatCount="indefinite" />
                  </circle>
                  <circle r="7" fill="#10b981" opacity="0.35">
                    <animateMotion path="M 250,150 L 85,45" dur="1.3s" repeatCount="indefinite" />
                  </circle>
                </>
              )}

              {/* Storage Node Laser Stream */}
              {archFeatures.storage && (
                <>
                  <path d="M 250,150 L 415,45" stroke="#3b82f6" strokeWidth="3" fill="none" strokeDasharray="10 6" className="animate-laser-flow" filter="url(#laser-glow-blue)" />
                  <circle r="4" fill="#2563eb">
                    <animateMotion path="M 250,150 L 415,45" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                  <circle r="7" fill="#3b82f6" opacity="0.35">
                    <animateMotion path="M 250,150 L 415,45" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                </>
              )}

              {/* Payments Node Laser Stream */}
              {archFeatures.payments && (
                <>
                  <path d="M 250,150 L 85,255" stroke="#a855f7" strokeWidth="3" fill="none" strokeDasharray="10 6" className="animate-laser-flow" filter="url(#laser-glow-purple)" />
                  <circle r="4" fill="#9333ea">
                    <animateMotion path="M 250,150 L 85,255" dur="1.4s" repeatCount="indefinite" />
                  </circle>
                  <circle r="7" fill="#a855f7" opacity="0.35">
                    <animateMotion path="M 250,150 L 85,255" dur="1.4s" repeatCount="indefinite" />
                  </circle>
                </>
              )}

              {/* DB Node Laser Stream */}
              {archFeatures.db && (
                <>
                  <path d="M 250,150 L 415,255" stroke="#14c9cb" strokeWidth="3" fill="none" strokeDasharray="10 6" className="animate-laser-flow" filter="url(#laser-glow-green)" />
                  <circle r="4" fill="#08b3e5">
                    <animateMotion path="M 250,150 L 415,255" dur="1.2s" repeatCount="indefinite" />
                  </circle>
                  <circle r="7" fill="#14c9cb" opacity="0.35">
                    <animateMotion path="M 250,150 L 415,255" dur="1.2s" repeatCount="indefinite" />
                  </circle>
                </>
              )}

              {/* Central Radar Pulse Rings */}
              <circle cx="250" cy="150" r="30" fill="none" stroke="#14c9cb" strokeWidth="1.5" className="animate-radar-wave" />
              <circle cx="250" cy="150" r="30" fill="none" stroke="#08b3e5" strokeWidth="1.5" className="animate-radar-wave-delayed" />

              {/* foreignObjects containing the interactive nodes */}
              {/* CENTRAL NODE */}
              <foreignObject x="185" y="112" width="130" height="76">
                <div className="bg-white/95 backdrop-blur-md border-2 border-emerald-500 p-2.5 rounded-2xl flex flex-col items-center justify-center text-center shadow-xl shadow-emerald-600/20 h-full w-full">
                  <div className="flex items-center gap-1.5">
                    <Smartphone className="h-4 w-4 text-emerald-600 animate-bounce" />
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-900">X2 Core Node</span>
                  </div>
                  <span className="text-[8px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full mt-1 border border-emerald-200">
                    Live Engine
                  </span>
                </div>
              </foreignObject>

              {/* NODE 1: CHAT */}
              <foreignObject x="25" y="15" width="120" height="55">
                <div className={`p-2 rounded-xl border-2 flex items-center justify-center space-x-2 transition-all duration-300 h-full w-full ${
                  archFeatures.chat 
                    ? 'bg-white border-emerald-500 text-slate-900 shadow-lg shadow-emerald-500/15' 
                    : 'bg-white/60 border-slate-200 text-slate-400'
                }`}>
                  <div className={`p-1 rounded-lg ${archFeatures.chat ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                    <Activity className="h-3.5 w-3.5" />
                  </div>
                  <div className="text-left">
                    <span className="text-[9px] font-bold block leading-none">WS Server</span>
                    <span className="text-[7px] text-emerald-600 font-semibold block mt-0.5">{archFeatures.chat ? 'Connected' : 'Offline'}</span>
                  </div>
                </div>
              </foreignObject>

              {/* NODE 2: STORAGE */}
              <foreignObject x="355" y="15" width="120" height="55">
                <div className={`p-2 rounded-xl border-2 flex items-center justify-center space-x-2 transition-all duration-300 h-full w-full ${
                  archFeatures.storage 
                    ? 'bg-white border-blue-500 text-slate-900 shadow-lg shadow-blue-500/15' 
                    : 'bg-white/60 border-slate-200 text-slate-400'
                }`}>
                  <div className={`p-1 rounded-lg ${archFeatures.storage ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                    <Cloud className="h-3.5 w-3.5" />
                  </div>
                  <div className="text-left">
                    <span className="text-[9px] font-bold block leading-none">AWS S3 CDN</span>
                    <span className="text-[7px] text-blue-600 font-semibold block mt-0.5">{archFeatures.storage ? 'Synced' : 'Offline'}</span>
                  </div>
                </div>
              </foreignObject>

              {/* NODE 3: PAYMENTS */}
              <foreignObject x="25" y="225" width="120" height="55">
                <div className={`p-2 rounded-xl border-2 flex items-center justify-center space-x-2 transition-all duration-300 h-full w-full ${
                  archFeatures.payments 
                    ? 'bg-white border-purple-500 text-slate-900 shadow-lg shadow-purple-500/15' 
                    : 'bg-white/60 border-slate-200 text-slate-400'
                }`}>
                  <div className={`p-1 rounded-lg ${archFeatures.payments ? 'bg-purple-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                    <CreditCard className="h-3.5 w-3.5" />
                  </div>
                  <div className="text-left">
                    <span className="text-[9px] font-bold block leading-none">Stripe Hub</span>
                    <span className="text-[7px] text-purple-600 font-semibold block mt-0.5">{archFeatures.payments ? 'Secured' : 'Offline'}</span>
                  </div>
                </div>
              </foreignObject>

              {/* NODE 4: DB CLUSTER */}
              <foreignObject x="355" y="225" width="120" height="55">
                <div className={`p-2 rounded-xl border-2 flex items-center justify-center space-x-2 transition-all duration-300 h-full w-full ${
                  archFeatures.db 
                    ? 'bg-white border-[#14c9cb] text-slate-900 shadow-lg shadow-[#14c9cb]/20' 
                    : 'bg-white/60 border-slate-200 text-slate-400'
                }`}>
                  <div className={`p-1 rounded-lg ${archFeatures.db ? 'bg-gradient-to-r from-[#10b981] to-[#14c9cb] text-white' : 'bg-slate-100 text-slate-400'}`}>
                    <Database className="h-3.5 w-3.5" />
                  </div>
                  <div className="text-left">
                    <span className="text-[9px] font-bold block leading-none">MySQL DB</span>
                    <span className="text-[7px] text-[#0d9488] font-semibold block mt-0.5">{archFeatures.db ? 'Clustered' : 'Offline'}</span>
                  </div>
                </div>
              </foreignObject>
            </svg>

            {/* Canvas Footer */}
            <div className="relative z-10 text-[10px] text-center text-slate-500 font-mono select-none -mt-3">
              Live Interactive Architecture Graph • Powered by X2 InfoTech Cloud Core
            </div>
          </div>
        </div>

        {/* ========================================================
            SECTION 6: TECH STACK SHOWCASE / BADGE WALL
           ======================================================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h3 className="text-2xl md:text-3xl font-bold text-brand-secondary">
              Modern & High-Performance <span className="text-brand-primary">Tech Stack</span>
            </h3>
            <p className="text-gray-600">
              Hover over our core software tools to see how they contribute to producing scalable products for our partners.
            </p>

            <div className="flex flex-wrap gap-3">
              {Object.keys(techDescriptions).map((tech) => (
                <div
                  key={tech}
                  onMouseEnter={() => setHoveredTech(tech)}
                  onMouseLeave={() => setHoveredTech(null)}
                  className={`px-4 py-2 rounded-full border text-sm font-semibold transition-all duration-200 cursor-default ${
                    hoveredTech === tech 
                      ? 'bg-brand-primary text-white border-brand-primary shadow-md scale-105' 
                      : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-brand-primary'
                  }`}
                >
                  {tech}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-brand-light border border-green-200 p-8 rounded-2xl min-h-[160px] flex items-center justify-center text-center">
            {hoveredTech ? (
              <div className="space-y-2 animate-fade-in">
                <p className="text-lg font-bold text-brand-secondary">{hoveredTech}</p>
                <p className="text-gray-600 text-sm leading-relaxed max-w-md mx-auto">
                  {techDescriptions[hoveredTech]}
                </p>
              </div>
            ) : (
              <div className="text-gray-400 italic text-sm animate-pulse">
                Hover over a technology tag on the left to see description...
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Quick Service Details Modal */}
      {activeServiceModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-xl w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto relative animate-scale-in">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="text-brand-primary bg-brand-light p-3 rounded-xl">
                  {activeServiceModal.icon}
                </div>
                <div>
                  <span className="text-xs font-bold text-brand-primary uppercase tracking-wide">
                    {activeServiceModal.badge}
                  </span>
                  <h3 className="text-xl font-bold text-brand-secondary">
                    {activeServiceModal.title}
                  </h3>
                  <p className="text-xs text-gray-500 font-medium">
                    {activeServiceModal.subtitle}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setActiveServiceModal(null)}
                className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-lg font-bold"
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed">
              {activeServiceModal.description}
            </p>

            <div className="space-y-2">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                Full Scope of Offerings:
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {activeServiceModal.offerings.map((offering, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-gray-50 text-xs text-gray-800">
                    <CheckCircle2 className="h-4 w-4 text-brand-primary flex-shrink-0 mt-0.5" />
                    <span>{offering}</span>
                  </div>
                ))}
              </div>
            </div>

            {activeServiceModal.technologies && (
              <div className="space-y-2">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Technologies:
                </h4>
                <div className="flex flex-wrap gap-1.5">
                  {activeServiceModal.technologies.map((tech, idx) => (
                    <span 
                      key={idx}
                      className="px-2.5 py-1 rounded-lg bg-green-50 text-green-800 text-xs font-semibold border border-green-200"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {activeServiceModal.benefits && (
              <div className="space-y-1.5">
                <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  Client ROI & Benefits:
                </h4>
                {activeServiceModal.benefits.map((b, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-xs text-gray-700">
                    <div className="w-1.5 h-1.5 bg-brand-primary rounded-full"></div>
                    <span>{b}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="pt-4 border-t border-gray-100 flex items-center justify-end gap-3">
              <button 
                onClick={() => setActiveServiceModal(null)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Close
              </button>
              <button 
                onClick={() => {
                  setActiveServiceModal(null);
                  window.location.hash = '#contact';
                }}
                className="px-5 py-2 rounded-xl text-xs font-bold bg-brand-primary text-white hover:bg-brand-dark transition-all shadow-md"
              >
                Request Architecture Blueprint
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeShowcase;
